import { Link } from "@inertiajs/react"

import { CostChart, type CostPoint } from "@/components/steel/cost-chart"
import { DataTable, Row, RowList, Td, Th, Thead, Tr } from "@/components/steel/table"
import { StatusBadge } from "@/components/steel"
import { formatCurrency, shortMoney } from "@/lib/utils"
import type { BurndownPoint, InvoiceRow, ProjectMilestone, Retrospective } from "@/pages/Projects/tabs/types"

function toChartPoints(burndown: BurndownPoint[]): CostPoint[] {
  return burndown.map((p) => ({
    label: new Date(`${p.week}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    planned: p.plannedCost,
    actual: p.actualCost,
    projected: p.projectedCost,
    low: p.projectedCostLow,
    high: p.projectedCostHigh,
    earnedValue: p.earnedValue,
  }))
}

function formatDate(value: string | null) {
  if (!value) return null
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function formatHours(value: number) {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`
}

function Retro({ retrospective }: { retrospective: Retrospective }) {
  const { finish, manHours, profit } = retrospective
  const days = finish.varianceDays

  return (
    <div className="border-b border-border px-6 py-4">
      <h2 className="mb-3 text-title font-semibold">Retrospective</h2>
      <DataTable minWidth={480}>
        <Thead>
          <Th>Measure</Th>
          <Th align="right">Planned</Th>
          <Th align="right">Actual</Th>
          <Th align="right">Variance</Th>
        </Thead>
        <tbody>
          <Tr>
            <Td strong>Man hours</Td>
            <Td align="right" num muted>{formatHours(manHours.planned)}</Td>
            <Td align="right" num>{formatHours(manHours.actual)}</Td>
            <Td align="right" num strong>{formatHours(manHours.variance)}</Td>
          </Tr>
          <Tr>
            <Td strong>Profit</Td>
            <Td align="right" num muted>{formatCurrency(profit.planned)}</Td>
            <Td align="right" num>{formatCurrency(profit.actual)}</Td>
            <Td align="right" num strong>{formatCurrency(profit.variance)}</Td>
          </Tr>
          <Tr>
            <Td strong>Finish</Td>
            <Td align="right" num muted>{formatDate(finish.planned) ?? "—"}</Td>
            <Td align="right" num>{formatDate(finish.actual) ?? "—"}</Td>
            <Td align="right" num strong>
              {days == null ? "—" : days === 0 ? "On time" : days < 0 ? `${-days}d early` : `${days}d late`}
            </Td>
          </Tr>
        </tbody>
      </DataTable>
    </div>
  )
}

export function OverviewTab({
  budget,
  burndown,
  expenseBreakdown,
  invoices,
  milestones,
  projectId,
  retrospective,
  spend,
}: {
  budget: number
  burndown: BurndownPoint[]
  expenseBreakdown: { category: string; amount: number }[]
  invoices: InvoiceRow[]
  milestones: ProjectMilestone[]
  projectId: string
  retrospective?: Retrospective | null
  spend: number
}) {
  const points = toChartPoints(burndown)
  const tickEvery = Math.max(1, Math.ceil(points.length / 8))
  const topCost = expenseBreakdown.length ? expenseBreakdown[0].amount : 0

  return (
    <div>
      {retrospective ? <Retro retrospective={retrospective} /> : null}

      <div className="grid border-b border-border lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="border-border px-6 py-4 lg:border-r">
          <div className="mb-1">
            <h2 className="text-title font-semibold">Cost against budget</h2>
            <p className="mt-0.5 text-meta text-muted-foreground">
              Hours at frozen rate plus expenses, by week
            </p>
          </div>
          <CostChart
            points={points}
            budget={budget}
            height={194}
            bands={5}
            tickEvery={tickEvery}
            projectedLabel="Projected ±1σ"
          />
        </div>

        <div className="px-6 py-4">
          <h2 className="text-title font-semibold">Where the money went</h2>
          <p className="mb-3.5 mt-0.5 text-meta text-muted-foreground">
            {shortMoney(spend)} committed to date
          </p>
          {expenseBreakdown.length === 0 ? (
            <p className="border-t border-border py-6 text-label text-muted-foreground">
              No cost recorded yet.
            </p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {expenseBreakdown.slice(0, 6).map((row, index) => (
                <div key={row.category}>
                  <div className="mb-1 flex items-baseline justify-between">
                    <span className="text-label">{row.category}</span>
                    <span className="num text-label text-muted-foreground">
                      {formatCurrency(row.amount)}
                    </span>
                  </div>
                  <div className="h-[5px] overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-[5px] rounded-full bg-foreground"
                      style={{
                        width: `${topCost > 0 ? (row.amount / topCost) * 100 : 0}%`,
                        opacity: 1 - index * 0.15,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2">
        <div className="border-border px-6 py-4 lg:border-r">
          <h2 className="mb-3 text-title font-semibold">Billing milestones</h2>
          {milestones.length === 0 ? (
            <p className="border-t border-border py-6 text-label text-muted-foreground">
              No milestones on this project.
            </p>
          ) : (
            <RowList>
              {milestones.map((m) => (
                <Row key={m.id}>
                  <span className="min-w-0 flex-1">
                    <span className="block text-body font-medium">{m.label}</span>
                    <span className="mt-0.5 block text-meta text-muted-foreground">
                      {m.billingPct}% of contract · bills at {m.completionPct}% complete
                    </span>
                  </span>
                  <span className="num text-body">{formatCurrency(m.amount)}</span>
                  <StatusBadge status={m.status} />
                </Row>
              ))}
            </RowList>
          )}
        </div>

        <div className="px-6 py-4 pb-7">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-title font-semibold">Invoices</h2>
            <Link
              href={`/projects/${projectId}/invoices/new`}
              className="text-label text-muted-foreground hover:text-foreground"
            >
              New invoice
            </Link>
          </div>
          {invoices.length === 0 ? (
            <p className="border-t border-border py-6 text-label text-muted-foreground">
              No invoices yet.
            </p>
          ) : (
            <RowList>
              {invoices.map((invoice) => (
                <Link
                  key={invoice.id}
                  href={`/invoices/${invoice.id}`}
                  className="flex w-full items-center gap-3 border-t border-border px-0.5 py-2.5 text-left hover:bg-secondary"
                >
                  <span className="min-w-0 flex-1">
                    <span className="num block text-body font-medium">{invoice.number}</span>
                    <span className="mt-0.5 block text-meta text-muted-foreground">
                      {formatDate(invoice.issueDate)
                        ? `Issued ${formatDate(invoice.issueDate)}`
                        : "Not issued"}
                    </span>
                  </span>
                  <span className="num text-body">{formatCurrency(invoice.total)}</span>
                  <StatusBadge status={invoice.status} />
                </Link>
              ))}
            </RowList>
          )}
        </div>
      </div>
    </div>
  )
}
